/**
 * Kern-datamodel van de Afkoppelcoach-app.
 *
 * Alle keuzelijsten staan hier als `const` arrays zodat ze op een plek
 * onderhouden worden en 1-op-1 matchen met de kolommen in supabase/schema.sql.
 * De TypeScript-types worden afgeleid van die arrays (single source of truth).
 */

/* ------------------------------------------------------------------ */
/* Kaartstatus (afgeleid, niet ingevuld)                               */
/* ------------------------------------------------------------------ */

/**
 * De kleur van een woning op de kaart. Dit is geen invulveld: de status
 * volgt uit het afkoppelbesluit en de realisatie, zodat de kaart nooit uit
 * de pas kan lopen met wat er in het formulier staat.
 */
export const KAART_STATUSSEN = [
  'Nog niet bezocht',
  'Niet thuis',
  'Nog geen keuze gemaakt',
  'Overweegt afkoppelen',
  'Wordt afgekoppeld',
  'Afgekoppeld',
  'Koppelt niet af',
] as const;

export type KaartStatus = (typeof KAART_STATUSSEN)[number];

/**
 * Welke vraag de kaart op dit moment beantwoordt.
 *
 *   'afkoppelen'   — gaat de hemelwaterafvoer van het riool af?
 *   'maatregelen'  — gebeurt er iets anders op het perceel?
 *
 * Twee lagen en geen twee "brillen". Een bril is een verborgen stand: je
 * ziet aan een stip niet door welke je kijkt. Een laag heeft een naam die
 * op het scherm blijft staan.
 *
 * De kleuren zijn in beide lagen dezelfde, want de trap is dezelfde: nog
 * niets gehoord, iemand denkt erover, het staat vast, het is gedaan, het
 * gebeurt niet. Alleen de woorden verschillen, en die staan in STATUS_TEKST.
 */
export type Kaartlaag = 'afkoppelen' | 'maatregelen';

export const LAAG_NAAM: Record<Kaartlaag, string> = {
  afkoppelen: 'Afkoppelen',
  maatregelen: 'Extra maatregelen',
};

/**
 * Wat de twee woorden betekenen. Eén keer vastgelegd, overal hetzelfde.
 *
 * Zonder deze grens legt iedere lezer hem ergens anders, en dan gaat een
 * coach die een regenton afspreekt "overweegt afkoppelen" aanvinken.
 */
/**
 * Wat de twee woorden in deze app betekenen.
 *
 * Let op wat hier NIET staat: dat een extra maatregel geen afkoppelen zou
 * zijn. Vakinhoudelijk is het dat wel — Aquo rekent infiltreren, hergebruik
 * en afvoeren naar oppervlaktewater allemaal tot afkoppelen, en een regenton
 * valt daar dus onder.
 *
 * Wat deze app onderscheidt is iets anders: verandert de aansluiting op het
 * riool, of blijft die zoals hij is. Dat is geen hydraulisch onderscheid maar
 * een praktisch: het eerste is werk aan het pand met een aannemer en een
 * subsidie erbij, het tweede doet de bewoner zelf in zijn tuin. Twee
 * besluiten, twee trajecten, vaak twee partijen.
 */
export const LAAG_UITLEG: Record<Kaartlaag, string> = {
  afkoppelen:
    'Het hemelwater gaat helemaal van het gemengde riool af. Dat kan op twee manieren: aansluiten op de nieuwe hemelwaterleiding, of volledig afkoppelen, waarbij al het water in de bodem infiltreert.',
  maatregelen:
    'Er gaat alleen minder water het riool in: tuin vergroenen, regenton, tegels eruit, wadi, groen dak, doorlatende verharding. Telt ook mee voor de afkoppelopgave.',
};

/**
 * De toets die een coach aan de deur kan toepassen.
 *
 * Kort genoeg om te onthouden en scherp genoeg om geen grensgeval over te
 * laten. Hij vervangt een eerdere versie die vroeg of het over de regenpijp
 * ging; die viel om op de regenton, want die zit óók op de regenpijp.
 *
 * Alles of minder: dat is de scheidslijn. Een regenton met een overloop op
 * het riool laat er alleen minder in en is dus een extra maatregel; dezelfde
 * regenton die overloopt in de tuin haalt die pijp er helemaal af en is dan
 * afkoppelen. Dat verschil is geen haarkloverij — het is precies wat er
 * hydraulisch toe doet.
 */
export const LAAG_TOETS =
  'Gaat het hemelwater helemaal van het gemengde riool af? Dan is het afkoppelen. Gaat er alleen minder water in? Dan is het een extra maatregel.';

/**
 * De zeven statuskleuren.
 *
 * Vier ervan vormen een trap die steeds donkerder wordt: wit, geel, cyaan,
 * donkerblauw. Dat is de weg ernaartoe — hoe donkerder, hoe verder. Cyaan is
 * "iemand denkt erover", donkerblauw is "het staat vast"; dat verschil zie je
 * aan de zwaarte van de kleur. De twee uitkomsten staan daar bewust buiten:
 * groen als het gelukt is, karmijn als er niet wordt afgekoppeld.
 *
 * Karmijn en geen signaalrood: een Nederlandse luchtfoto staat vol rode
 * dakpannen, en signaalrood verdwijnt daarin.
 *
 * Grijs staat buiten die trap. Het is de enige kleur die niets over de
 * afkoppeling zegt: er is iemand geweest, er is nog geen keuze. Grijs
 * verschilt van wit alleen in lichtheid, en dat is het ene kanaal dat bij
 * elke vorm van kleurenblindheid overeind blijft. Een blauwgrijs zou passender
 * ogen bij de huisstijl, maar valt bij protanopie samen met cyaan: #9aa3b8
 * kwam daar op 12,7 uit, waar dit neutrale grijs op 29,8 zit.
 *
 * Alle zeven worden als hetzelfde rondje getekend, dus kleur draagt de betekenis
 * alleen. Daarom zijn ze doorgerekend op onderlinge afstand in Lab, ook zoals
 * iemand met deuteranopie of protanopie ze ziet. Het krapste paar in het hele
 * palet zit op 27 — ruim boven de grens waarop je twee kleuren nog naast
 * elkaar moet leggen om ze te scheiden. Grijs verandert daar niets aan: zijn
 * krapste paar ligt op 29,8, ruim boven die 27.
 *
 * Karmijn is daarvoor flink verdiept, van #c0143c naar #6b0a20. Met de
 * heldere variant vielen groen en karmijn bij deuteranopie op 11 samen: het
 * ene paar met tegengestelde betekenis dat nooit mag verwarren.
 *
 * Deze waarden staan ook als --kleur-succes, --kleur-fout, --kleur-waarschuwing
 * en --kleur-info in src/styles/tokens.css. Dat is opzet: groen betekent in
 * deze app één ding, of het nu een woning op de kaart is of een melding dat
 * het opslaan gelukt is. Verander je er hier één, verander hem daar dan mee.
 */
export const STATUS_KLEUREN: Record<KaartStatus, string> = {
  'Nog niet bezocht': '#ffffff',
  'Niet thuis': '#f4af00',
  'Nog geen keuze gemaakt': '#75767a',
  'Overweegt afkoppelen': '#00afd3',
  'Wordt afgekoppeld': '#1c2445',
  Afgekoppeld: '#00a243',
  'Koppelt niet af': '#6b0a20',
};

/** Achtergrondvlak van een badge of chip in die status. */
export const STATUS_KLEUREN_LICHT: Record<KaartStatus, string> = {
  'Nog niet bezocht': '#eef1f7',
  'Niet thuis': '#fff2c7',
  'Nog geen keuze gemaakt': '#e4e4e6',
  'Overweegt afkoppelen': '#ccf0f5',
  'Wordt afgekoppeld': '#dfe3f0',
  Afgekoppeld: '#b2e3c7',
  'Koppelt niet af': '#fadce3',
};

/** Tekst op dat lichte vlak, donker genoeg om buiten te lezen. */
export const STATUS_KLEUREN_TEKST: Record<KaartStatus, string> = {
  'Nog niet bezocht': '#4a5170',
  'Niet thuis': '#7a5600',
  'Nog geen keuze gemaakt': '#4d4f55',
  'Overweegt afkoppelen': '#00647a',
  'Wordt afgekoppeld': '#1c2445',
  Afgekoppeld: '#00602a',
  'Koppelt niet af': '#a00f32',
};

/**
 * De woorden bij elke kleur, per laag.
 *
 * Dit is de enige plek waar een status een naam krijgt. Nergens in de app
 * wordt de sleutel zelf op het scherm gezet, want dan zou in de laag Extra
 * maatregelen "Overweegt afkoppelen" komen te staan bij een bewoner die
 * alleen een regenton wil — precies het misverstand dat we oplossen.
 *
 * De sleutels dragen de woorden van de laag Afkoppelen. Dat is geen
 * toeval: dat is de hoofdvraag van het project, en het is de tekst die in
 * de export en op een badge terechtkomt.
 */
export const STATUS_TEKST: Record<Kaartlaag, Record<KaartStatus, string>> = {
  afkoppelen: {
    'Nog niet bezocht': 'Nog niet bezocht',
    'Niet thuis': 'Niet thuis',
    'Nog geen keuze gemaakt': 'Nog geen keuze',
    'Overweegt afkoppelen': 'Overweegt afkoppelen',
    'Wordt afgekoppeld': 'Wordt afgekoppeld',
    Afgekoppeld: 'Afgekoppeld',
    'Koppelt niet af': 'Koppelt niet af',
  },
  maatregelen: {
    'Nog niet bezocht': 'Nog niet bezocht',
    'Niet thuis': 'Niet thuis',
    'Nog geen keuze gemaakt': 'Nog geen keuze',
    'Overweegt afkoppelen': 'Overweegt maatregelen',
    'Wordt afgekoppeld': 'Maatregelen afgesproken',
    Afgekoppeld: 'Maatregelen genomen',
    'Koppelt niet af': 'Geen maatregelen',
  },
};

/* ------------------------------------------------------------------ */
/* Keuzelijsten                                                        */
/* ------------------------------------------------------------------ */

export const EIGENDOMSVORMEN = ['Koop', 'Huur', 'Onbekend'] as const;
export type Eigendom = (typeof EIGENDOMSVORMEN)[number];

export const UITKOMSTEN_BEZOEK = [
  'Gesproken met bewoner',
  'Aangebeld, niet thuis',
  'Wil niet meewerken',
  'Afspraak gemaakt voor later',
  'Anders',
] as const;
export type UitkomstBezoek = (typeof UITKOMSTEN_BEZOEK)[number];

/** Adviesmaatregelen; per tuin kunnen er meerdere worden aangevinkt. */
export const ADVIEZEN = [
  'Regenton plaatsen',
  'Tegels eruit, groen erin',
  'Infiltratiekrat / grindkoffer',
  'Waterdoorlatende verharding',
  'Groen dak',
  'Hemelwaterafvoer afkoppelen (voorzijde)',
  'Hemelwaterafvoer afkoppelen (achterzijde)',
  'Regenwaterschutting',
  'Vijver / wadi / verlaagd plantvak',
  'Aansluiten op oppervlaktewater',
  'Geen maatregel mogelijk',
  'Anders (zie toelichting)',
] as const;
export type Advies = (typeof ADVIEZEN)[number];

/**
 * Wat er met deze woning gaat gebeuren.
 *
 * "Gaat afkoppelen" is er bewust uit. Dat claimde een toezegging die aan de
 * deur niemand doet: van de 76 woningen stond er precies één op, tegenover
 * 41 in het vage middenbakje. Een coach die een enthousiaste bewoner sprak
 * moest kiezen tussen een belofte die er niet was en "twijfelt nog", en koos
 * het laatste. Zo leek het project matiger te lopen dan het deed.
 *
 * De harde toezegging staat trouwens al ergens anders: bij de subsidie en bij
 * de realisatie. Dit veld gaat over de bedoeling.
 *
 * Er is geen middenweg meer. Wie twijfelt maar wel belangstelling heeft is een
 * overweger; wie zegt het niet te doen is een nee. Een coach die aan de deur
 * staat hoort dat verschil, en een bakje ertussen leverde alleen maar een
 * berg registraties op waar niemand iets aan had: 41 van de 76.
 *
 * "Nog geen keuze gemaakt" heette eerst "Nog niet besproken". Dat dekte de
 * praktijk niet: een coach die aanbelt en een kind aan de deur krijgt dat
 * zegt dat zijn ouders op vakantie zijn, hééft gesproken. Er is alleen niets
 * besloten. Met de oude naam moest zo'n adres of onterecht op "niet thuis"
 * of op een keuze die niemand gemaakt had.
 *
 * Dit veld gaat alleen over het PAND. Bij een huurwoning wordt het dus niet
 * gevraagd: de regenpijp is niet van de bewoner, en hem daarover laten
 * antwoorden is precies waar de verwarring vandaan kwam. Wat de huurder zelf
 * wil staat in maatregelenbesluit.
 */
export const AFKOPPELBESLUITEN = [
  'Nog geen keuze gemaakt',
  'Overweegt afkoppelen',
  'Koppelt niet af',
] as const;
export type Afkoppelbesluit = (typeof AFKOPPELBESLUITEN)[number];

/**
 * Wat de bewoner zelf op het perceel doet, naast de afkoppeling.
 *
 * Dit bestond niet, en dat was het gat. Een bewoner die zegt "helemaal
 * afkoppelen hoeft van mij niet, maar een regenton wil ik wel" moest op
 * "gaat niet afkoppelen" — onwaar — of op "overweegt afkoppelen", wat meer
 * belooft dan er gezegd is. Bij een huurwoning was het nog scheever: daar
 * werd het afkoppelbesluit stilzwijgend voor de tuin gebruikt, zodat
 * hetzelfde woord bij koop en huur iets anders betekende.
 *
 * Dezelfde drie antwoorden als bij het afkoppelbesluit. Eén patroon om te
 * onthouden, in plaats van twee lijstjes.
 */
export const MAATREGELENBESLUITEN = [
  'Nog geen keuze gemaakt',
  'Overweegt maatregelen',
  'Geen maatregelen',
] as const;
export type Maatregelenbesluit = (typeof MAATREGELENBESLUITEN)[number];

export const SUBSIDIE_STATUSSEN = [
  'Niet van toepassing',
  'Nog niet aangevraagd',
  'Aanvraag in voorbereiding',
  'Aanvraag ingediend',
  'Toegekend',
  'Afgewezen',
] as const;
export type SubsidieStatus = (typeof SUBSIDIE_STATUSSEN)[number];

export const REALISATIE_STATUSSEN = [
  'Niet gestart',
  'Gepland',
  'In uitvoering',
  'Uitgevoerd',
  'Geannuleerd',
] as const;
export type RealisatieStatus = (typeof REALISATIE_STATUSSEN)[number];

/**
 * Hoe het contact met de bewoner verliep.
 *
 * Dit onderscheid bestaat omdat niet elk nieuw gegeven een bezoek is: een
 * bewoner die mailt dat de subsidie rond is, is voortgang zonder dat er
 * iemand aan de deur is geweest. Zonder deze soort zou het dashboard
 * bezoeken tellen die nooit hebben plaatsgevonden.
 */
export const CONTACT_SOORTEN = [
  'Bezoek aan de deur',
  'Telefonisch contact',
  'E-mail van de bewoner',
  'Brief of folder bezorgd',
  'Informatie via de gemeente',
  'Anders',
] as const;
export type ContactSoort = (typeof CONTACT_SOORTEN)[number];

export const FOTO_SOORTEN = ['Voorkant', 'Achterkant', 'Overig'] as const;
export type FotoSoort = (typeof FOTO_SOORTEN)[number];

/* ------------------------------------------------------------------ */
/* Deelobjecten                                                        */
/* ------------------------------------------------------------------ */

export interface Adres {
  straat: string;
  huisnummer: string;
  huisletter?: string;
  toevoeging?: string;
  postcode: string;
  plaats: string;
}

/** Voor- en achtertuin hebben exact hetzelfde veldenpakket. */
export interface TuinAdvies {
  aanwezig: boolean;
  /** Meerdere adviezen tegelijk zijn normaal (bijv. regenton + tegels eruit). */
  advies: Advies[];
  /** Indicatieve berging / af te koppelen hoeveelheid in kubieke meter. */
  bergingM3: number | null;
  toelichting: string;
}

export const LEGE_TUIN: TuinAdvies = {
  aanwezig: true,
  advies: [],
  bergingM3: null,
  toelichting: '',
};

export interface Bewoner {
  naam: string;
  telefoon: string;
  email: string;
  /** AVG: expliciete toestemming voor het vastleggen van persoonsgegevens. */
  toestemmingPersoonsgegevens: boolean;
  toelichting: string;
}

export const LEGE_BEWONER: Bewoner = {
  naam: '',
  telefoon: '',
  email: '',
  toestemmingPersoonsgegevens: false,
  toelichting: '',
};

export interface Foto {
  id: string;
  bestandsnaam: string;
  /** Pad in de opslagplek, bijv. "0779010000000011/voorkant_2026....jpg". */
  pad: string;
  soort: FotoSoort;
  /** Wat erop staat of waarom hij gemaakt is; door de coach ingevuld. */
  toelichting: string;
  grootteBytes: number;
  toegevoegdOp: string; // ISO
  /** Nog niet geupload; staat alleen lokaal als data-url. */
  lokaal?: boolean;
  dataUrl?: string;
}

/**
 * Eén moment waarop er contact was over dit adres.
 *
 * De lijst groeit: het eerste bezoek, later een telefoontje, daarna een
 * e-mail. Het advies en het besluit staan hier bewust niet in — dat is de
 * huidige stand van zaken van de woning, niet iets dat bij één moment hoort.
 * In januari een regenton adviseren en in april een wadi betekent dat het
 * advies is bijgesteld, niet dat er twee adviezen zijn.
 */
export interface Contactmoment {
  id: string;
  datum: string; // yyyy-mm-dd
  soort: ContactSoort;
  coach: string;
  uitkomst: UitkomstBezoek | '';
  toelichting: string;
}

/* ------------------------------------------------------------------ */
/* Woning (BAG-object op de kaart)                                     */
/* ------------------------------------------------------------------ */

export interface Woning {
  /** BAG verblijfsobject-identificatie (16 cijfers). */
  bagId: string;
  adres: Adres;
  latitude: number;
  longitude: number;
  bouwjaar?: number;
  oppervlakteM2?: number;
}

/* ------------------------------------------------------------------ */
/* Registratie (hoofdentiteit == 1 rij in de database)                 */
/* ------------------------------------------------------------------ */

export type SyncState = 'synced' | 'pending' | 'error';

export interface Registratie {
  id: string;

  // -- Woning (komt van de kaart) ------------------------------------
  bagId: string;
  adres: Adres;
  latitude: number;
  longitude: number;
  eigendom: Eigendom | '';

  // -- Contact -------------------------------------------------------
  /** Alle momenten waarop er contact was, oudste eerst. */
  contactmomenten: Contactmoment[];

  /**
   * Spiegel van het meest recente contactmoment.
   *
   * Deze vier velden staan er dubbel in, en dat is een bewuste keuze: het
   * dashboard sorteert erop, de export gebruikt ze en de kaartkleur leest
   * eruit af. Ze worden bij het opslaan opnieuw afgeleid uit de lijst, zodat
   * ze niet uiteen kunnen lopen. Bewerk dus de lijst, niet deze velden.
   */
  datumBezoek: string; // yyyy-mm-dd
  coach: string;
  uitkomstBezoek: UitkomstBezoek | '';
  uitkomstToelichting: string;

  // -- Bewoner -------------------------------------------------------
  bewoner: Bewoner;

  // -- Tuinen --------------------------------------------------------
  voortuin: TuinAdvies;
  achtertuin: TuinAdvies;

  // -- Resultaat -----------------------------------------------------
  /** Alleen over het pand; bij huur beslist de corporatie en blijft dit leeg. */
  afkoppelbesluit: Afkoppelbesluit | '';
  /** Wat de bewoner zelf op het perceel doet; geldt bij koop én huur. */
  maatregelenbesluit: Maatregelenbesluit | '';
  subsidieStatus: SubsidieStatus | '';
  realisatieStatus: RealisatieStatus | '';
  datumVervolg: string; // yyyy-mm-dd of leeg
  algemeneToelichting: string;

  // -- Fotos ---------------------------------------------------------
  fotos: Foto[];

  // -- Meta ----------------------------------------------------------
  aangemaaktOp: string; // ISO datetime
  gewijzigdOp: string; // ISO datetime
  /**
   * Telt op bij elke opslag. Versie 1 is het eerste bezoek; alles daarboven
   * betekent dat de coach later is teruggeweest. De vorige versies staan in
   * de historie, zodat de voortgang te volgen is.
   */
  versie: number;
  syncState: SyncState;
  syncFout?: string;
}

/* ------------------------------------------------------------------ */
/* Afgeleide status                                                    */
/* ------------------------------------------------------------------ */

/**
 * Hoe ver het gesprek met de bewoner is, los van enig besluit.
 *
 * Drie standen die in beide lagen hetzelfde betekenen, en die dus maar één
 * keer bepaald hoeven te worden.
 */
function contactStand(
  registratie: Registratie | undefined
): 'Niet thuis' | 'Nog geen keuze gemaakt' | 'Nog niet bezocht' {
  if (!registratie) return 'Nog niet bezocht';

  // Aangebeld maar niemand aangetroffen. 'Niemand thuis' is de oude
  // schrijfwijze; die blijft meetellen zodat registraties van vóór de
  // hernoeming de goede kleur houden.
  if (
    registratie.uitkomstBezoek === 'Aangebeld, niet thuis' ||
    (registratie.uitkomstBezoek as string) === 'Niemand thuis'
  ) {
    return 'Niet thuis';
  }

  /*
   * Er is iemand geweest en het contact heeft iets opgeleverd, maar er ligt
   * nog geen keuze. Dat is een eigen stand, geen leeg vakje: het adres is af
   * te strepen voor wie de route plant, en nog open voor wie wil weten
   * hoeveel woningen meedoen.
   *
   * De uitkomst van het contact telt, niet het bestaan van een registratie.
   * Een formulier dat is aangemaakt en verder leeg is gebleven heeft niets
   * opgeleverd en staat dus nog op "nog niet bezocht".
   */
  const contactHeeftIetsOpgeleverd =
    Boolean(registratie.uitkomstBezoek) ||
    registratie.contactmomenten.some((moment) => Boolean(moment.uitkomst));

  return contactHeeftIetsOpgeleverd ? 'Nog geen keuze gemaakt' : 'Nog niet bezocht';
}

/**
 * Laag Afkoppelen: gaat de hemelwaterafvoer van het riool af?
 *
 * Volgorde is bewust. Uitgevoerd werk wint van alles daarvoor. Daarna telt
 * wat vaststaat, dan wat iemand overweegt, en pas als laatste hoe ver het
 * gesprek is.
 *
 * Bij een huurwoning staat het antwoord al vast voordat een coach aanbelt:
 * de corporatie heeft laten weten dat zij deze panden afkoppelt. Dat is een
 * projectgegeven en geen waarneming aan de deur, en daarom vraagt het
 * formulier er ook niet naar.
 *
 * Bij een koopwoning telt een toegekende subsidie als vaststaand. Dat is
 * geen belofte aan de deur maar een besluit met een dossiernummer — het
 * enige harde punt dat een koopwoning in deze fase kan bereiken.
 */
export function afkoppelStatus(
  registratie: Registratie | undefined,
  huur: boolean
): KaartStatus {
  if (registratie?.realisatieStatus === 'Uitgevoerd') return 'Afgekoppeld';
  if (huur) return 'Wordt afgekoppeld';
  if (!registratie) return 'Nog niet bezocht';

  if (registratie.subsidieStatus === 'Toegekend') return 'Wordt afgekoppeld';

  const besluit = registratie.afkoppelbesluit as string;

  // 'Gaat niet afkoppelen', 'Twijfelt nog' en 'Gaat afkoppelen' zijn oude
  // schrijfwijzen. Die blijven meetellen, ook voordat het opruimscript is
  // gedraaid.
  if (besluit === 'Koppelt niet af' || besluit === 'Gaat niet afkoppelen') {
    return 'Koppelt niet af';
  }
  if (besluit === 'Overweegt afkoppelen' || besluit === 'Twijfelt nog' || besluit === 'Gaat afkoppelen') {
    return 'Overweegt afkoppelen';
  }

  return contactStand(registratie);
}

/**
 * Laag Extra maatregelen: gebeurt er iets anders op het perceel?
 *
 * Dezelfde kleuren, een andere vraag. Daarom staan hier sleutels die in hun
 * naam over afkoppelen gaan: die naam is de kleur, niet de tekst. Wat een
 * lezer te zien krijgt staat in STATUS_TEKST.maatregelen.
 *
 * Koop en huur lezen hier gelijk, want de tuin is van de bewoner — of hij
 * die nu huurt of bezit. Dat is meteen het punt van de splitsing: dit was
 * het enige wat bij een huurwoning aan de deur te halen viel, en het stond
 * in hetzelfde veld als een vraag die daar helemaal niet gesteld kon worden.
 *
 * Donkerblauw en groen komen hier nog niet voor. Dat de corporatie zelf
 * vergroent en of een regenton daadwerkelijk is geplaatst, leggen we
 * (nog) nergens vast; een legendaregel die altijd op nul staat is ruis.
 */
export function maatregelenStatus(registratie: Registratie | undefined): KaartStatus {
  if (!registratie) return 'Nog niet bezocht';

  const besluit = registratie.maatregelenbesluit;
  if (besluit === 'Overweegt maatregelen') return 'Overweegt afkoppelen';
  if (besluit === 'Geen maatregelen') return 'Koppelt niet af';

  return contactStand(registratie);
}

/** De status in de laag waar je naar kijkt. */
export function statusInLaag(
  registratie: Registratie | undefined,
  huur: boolean,
  laag: Kaartlaag
): KaartStatus {
  return laag === 'afkoppelen'
    ? afkoppelStatus(registratie, huur)
    : maatregelenStatus(registratie);
}

/** Statussen die in deze laag kunnen voorkomen; de rest laten we weg. */
export function statussenVoorLaag(laag: Kaartlaag): readonly KaartStatus[] {
  if (laag === 'afkoppelen') return KAART_STATUSSEN;
  return KAART_STATUSSEN.filter(
    (status) => status !== 'Wordt afgekoppeld' && status !== 'Afgekoppeld'
  );
}

/**
 * Contactmomenten op volgorde van datum, oudste eerst.
 *
 * De lijst staat in de volgorde waarin de coach hem heeft opgebouwd, en dat
 * is meestal ook de tijdvolgorde. Meestal: een telefoontje van vorige week
 * dat pas vandaag wordt bijgeschreven komt achteraan te staan terwijl het
 * eerder gebeurde. Voor alles waar de volgorde iets betekent — het verloop
 * in de export, wat het laatste contact was — telt de datum, niet het moment
 * van invoeren.
 *
 * Momenten met dezelfde datum houden hun onderlinge volgorde; Array.sort is
 * stabiel.
 */
export function contactmomentenOpDatum(momenten: Contactmoment[]): Contactmoment[] {
  return [...momenten].sort((a, b) => a.datum.localeCompare(b.datum));
}

/** Het meest recente contactmoment, of niets als er nog geen is. */
export function laatsteContactmoment(registratie: Registratie): Contactmoment | undefined {
  const opVolgorde = contactmomentenOpDatum(registratie.contactmomenten);
  return opVolgorde[opVolgorde.length - 1];
}

/** Hoe vaak er daadwerkelijk iemand aan de deur is geweest. */
export function aantalBezoeken(registratie: Registratie): number {
  return registratie.contactmomenten.filter((c) => c.soort === 'Bezoek aan de deur').length;
}

/**
 * Zet de gespiegelde bezoekvelden gelijk aan het laatste contactmoment.
 *
 * Wordt aangeroepen vlak voor het opslaan. Zo is er één plek waar die
 * afleiding gebeurt en kan de spiegel niet achterlopen op de lijst.
 */
export function spiegelLaatsteContact(registratie: Registratie): Registratie {
  const laatste = laatsteContactmoment(registratie);
  if (!laatste) return registratie;

  return {
    ...registratie,
    datumBezoek: laatste.datum,
    coach: laatste.coach,
    uitkomstBezoek: laatste.uitkomst,
    uitkomstToelichting: laatste.toelichting,
  };
}

/**
 * Velden waarbij een wijziging zonder nieuw contactmoment verdacht is.
 *
 * Een foto toevoegen of een typefout herstellen kan prima zonder dat er
 * iemand met de bewoner heeft gesproken. Een besluit dat omslaat niet: daar
 * is iets voor gebeurd, en dat hoort vastgelegd te worden.
 */
export function heeftInhoudelijkeVoortgang(oud: Registratie, nieuw: Registratie): boolean {
  const gelijk = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

  return (
    oud.afkoppelbesluit !== nieuw.afkoppelbesluit ||
    oud.maatregelenbesluit !== nieuw.maatregelenbesluit ||
    oud.subsidieStatus !== nieuw.subsidieStatus ||
    oud.realisatieStatus !== nieuw.realisatieStatus ||
    !gelijk(oud.voortuin.advies, nieuw.voortuin.advies) ||
    !gelijk(oud.achtertuin.advies, nieuw.achtertuin.advies)
  );
}

/** Totale indicatieve berging van voor- en achtertuin samen. */
export function totaleBergingM3(registratie: Registratie): number {
  return (registratie.voortuin.bergingM3 ?? 0) + (registratie.achtertuin.bergingM3 ?? 0);
}

/* ------------------------------------------------------------------ */
/* Statistiek                                                          */
/* ------------------------------------------------------------------ */

export interface DashboardStats {
  totaalWoningen: number;

  /**
   * Twee reeksen, elk per status geteld, en allebei optellend tot
   * totaalWoningen.
   *
   * Dat ze allebei optellen tot hetzelfde totaal is de hele truc. Eerder
   * telde de ene reeks woningen en de andere alleen huurders, en dan kun je
   * twee getallen naast elkaar leggen die elkaar lijken tegen te spreken.
   * Nu is elke woning in beide reeksen precies één keer vertegenwoordigd.
   */
  afkoppelen: Record<KaartStatus, number>;
  maatregelen: Record<KaartStatus, number>;

  subsidieIngediend: number;
  /** Adressen met meer dan één contactmoment. */
  meerdereContacten: number;
  /** Hoe vaak er daadwerkelijk iemand aan de deur is geweest. */
  totaalBezoeken: number;
  totaleBergingM3: number;
}


/* ------------------------------------------------------------------ */
/* Historie: eerdere versies van een registratie                       */
/* ------------------------------------------------------------------ */

/**
 * Een eerdere versie van een registratie.
 *
 * Bij elke opslag wordt de vorige stand weggeschreven. Zo blijft zichtbaar
 * hoe een adres zich heeft ontwikkeld: in januari nog "twijfelt nog", in
 * april "gaat afkoppelen", in september uitgevoerd. De nieuwste versie staat
 * in `registraties`; alles daarvoor staat hier.
 */
export interface RegistratieVersie {
  id: string;
  registratieId: string;
  /** Welke versie dit was; loopt op vanaf 1. */
  versie: number;
  /** Wanneer deze versie is vervangen door een nieuwere. */
  vervangenOp: string; // ISO datetime
  /** De coach die deze versie had ingevuld. */
  coach: string;
  /** De volledige stand van die versie. */
  momentopname: Registratie;
}

/** Korte omschrijving van wat er tussen twee versies is veranderd. */
export interface Verschil {
  veld: string;
  van: string;
  naar: string;
}

/**
 * Toont alleen wat inhoudelijk verschilt.
 *
 * De gespiegelde bezoekvelden staan hier bewust niet bij: die volgen uit het
 * laatste contactmoment en zouden bij elk nieuw contact meeveranderen. Dat
 * levert regels op als "datum bezoek gewijzigd" terwijl er alleen een
 * telefoontje is bijgeschreven — ruis die het overzicht juist onleesbaar
 * maakt.
 */
export function vergelijkVersies(oud: Registratie, nieuw: Registratie): Verschil[] {
  const tekstVan = (waarde: unknown): string => {
    if (waarde === null || waarde === undefined || waarde === '') return '—';
    if (typeof waarde === 'boolean') return waarde ? 'ja' : 'nee';
    if (Array.isArray(waarde)) return waarde.length ? waarde.join(', ') : '—';
    return String(waarde);
  };

  const velden: [string, unknown, unknown][] = [
    ['Koop of huur', oud.eigendom, nieuw.eigendom],
    ['Voortuin aanwezig', oud.voortuin.aanwezig, nieuw.voortuin.aanwezig],
    ['Advies voortuin', oud.voortuin.advies, nieuw.voortuin.advies],
    ['Berging voortuin (m³)', oud.voortuin.bergingM3, nieuw.voortuin.bergingM3],
    ['Toelichting voortuin', oud.voortuin.toelichting, nieuw.voortuin.toelichting],
    ['Achtertuin aanwezig', oud.achtertuin.aanwezig, nieuw.achtertuin.aanwezig],
    ['Advies achtertuin', oud.achtertuin.advies, nieuw.achtertuin.advies],
    ['Berging achtertuin (m³)', oud.achtertuin.bergingM3, nieuw.achtertuin.bergingM3],
    ['Toelichting achtertuin', oud.achtertuin.toelichting, nieuw.achtertuin.toelichting],
    ['Afkoppelen van het pand', oud.afkoppelbesluit, nieuw.afkoppelbesluit],
    ['Extra maatregelen', oud.maatregelenbesluit, nieuw.maatregelenbesluit],
    ['Status subsidieaanvraag', oud.subsidieStatus, nieuw.subsidieStatus],
    ['Status realisatie', oud.realisatieStatus, nieuw.realisatieStatus],
    ['Datum vervolgafspraak', oud.datumVervolg, nieuw.datumVervolg],
    ['Algemene toelichting', oud.algemeneToelichting, nieuw.algemeneToelichting],
    ["Aantal foto's", oud.fotos.length, nieuw.fotos.length],
  ];

  return velden
    .map(([veld, van, naar]) => ({ veld, van: tekstVan(van), naar: tekstVan(naar) }))
    .filter((verschil) => verschil.van !== verschil.naar);
}
